import React from 'react';
import { Box, Typography, Stack, Chip, alpha, useTheme } from '@mui/material';
import {
  LOGIN_PORTAL_THEMES,
  LOGIN_PORTAL_TEMPLATE_IDS,
  LOGIN_PORTAL_DEFAULT_TEMPLATE
} from '../../theme/loginPortalThemes';

function ThemeColorSwatches({ themeId, compact }) {
  const th = LOGIN_PORTAL_THEMES[themeId] || LOGIN_PORTAL_THEMES[LOGIN_PORTAL_DEFAULT_TEMPLATE];
  const swatches = [
    { label: 'Page', color: th.background, gradient: true },
    { label: 'Panel', color: th.panelBg },
    { label: 'Text', color: th.textPrimary },
    { label: 'Accent', color: th.decorColor1 }
  ];
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: compact ? 0.5 : 1 }}>
      {swatches.map((s) => (
        <Chip
          key={s.label}
          size="small"
          label={s.label}
          sx={{
            height: 22,
            fontSize: '0.65rem',
            fontWeight: 600,
            color: th.textPrimary,
            bgcolor: s.gradient ? 'transparent' : s.color,
            background: s.gradient ? s.color : undefined,
            border: '1px solid',
            borderColor: alpha(th.textPrimary, 0.15),
            '& .MuiChip-label': { px: 0.75 }
          }}
        />
      ))}
    </Stack>
  );
}

/**
 * Visual theme picker shared by landing page, query portal, and login.
 * @param {{ selectedId: string, onSelect: (id: string) => void, showDetails?: boolean, columns?: 'auto'|'3'|'5' }} props
 */
export default function PortalTemplatePickerGrid({ selectedId, onSelect, showDetails = false, columns = 'auto' }) {
  const muiTheme = useTheme();
  const minWidth = columns === '5' ? 120 : columns === '3' ? 160 : 148;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
      {LOGIN_PORTAL_TEMPLATE_IDS.map((id) => {
        const th = LOGIN_PORTAL_THEMES[id];
        const selected = selectedId === id;
        const barIsLight = ['light', 'aqua', 'orchid'].includes(id);
        return (
          <Box
            key={id}
            component="button"
            type="button"
            onClick={() => onSelect(id)}
            sx={{
              flex: columns === '5' ? '1 1 18%' : '1 1 auto',
              minWidth,
              maxWidth: columns === '5' ? '20%' : '100%',
              p: showDetails ? 1.5 : 1.25,
              borderRadius: 2,
              border: '2px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              bgcolor: 'background.paper',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.15s',
              boxShadow: selected ? `0 0 0 3px ${alpha(muiTheme.palette.primary.main, 0.22)}` : 'none',
              transform: selected ? 'translateY(-2px)' : 'none',
              '&:hover': {
                borderColor: selected ? 'primary.main' : alpha(muiTheme.palette.primary.main, 0.35),
                boxShadow: 2
              }
            }}
          >
            <Box
              sx={{
                height: showDetails ? 56 : 48,
                borderRadius: 1,
                mb: 1,
                background: th.background,
                boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  right: 8,
                  height: 14,
                  borderRadius: 0.5,
                  bgcolor: th.panelBg,
                  border: `1px solid ${th.panelBorder}`,
                  boxShadow: th.panelShadow
                }}
              />
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }}>
              {th.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.35 }}>
              {th.description}
            </Typography>
            {showDetails && (
              <>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary', fontSize: '0.68rem' }}>
                  Nav: {barIsLight ? 'Light toolbar' : 'Dark toolbar'} · Cards use panel palette
                </Typography>
                <ThemeColorSwatches themeId={id} compact />
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
